// 事件 snapshot 縮圖的本地持久快取(對齊 native iOS/Android 的本地 90 天自刪)。
//
// 事件清單 / 回放時間軸對 has_snapshot 的事件顯示一張縮圖。原本只跟後端拿一個
// ~300s 短效簽名 URL 塞進 <img>,重整就沒了、每次都得重簽 + 重下載。這裡加一個
// IndexedDB 物件庫把縮圖 bytes 留在本機:命中就零網路、重整仍在,並在開頁時自動
// 清掉超過 SNAPSHOT_TTL_MS(90 天)的舊縮圖。
//
// 為何存 data: URL 字串而非 blob:webapp 的 CSP 是 `img-src 'self' data: <api>`——
// 沒放行 `blob:`(那只在 media-src),所以 <img src=blob:...> 會被 CSP 擋;data: URL
// 直接可用、也免 object-URL 生命週期管理。bytes 靠同源 API 的 CORS 抓得到
// (snapshot 端點回 access-control-allow-origin: <webapp>)。
//
// 設計要點(比照 galleryDb.ts):
//   - store `snapshots` keyPath=eid,索引 savedAt(供 purge 掃過期)。
//   - 環境無 indexedDB(SSR / jsdom 測試)時全部優雅降級:寫入 no-op、讀取回 null。

const DB_NAME = 'ls-home-event-snapshots';
const DB_VERSION = 1;
const STORE = 'snapshots';

/** 一筆快取的縮圖。dataUrl 為 `data:image/...;base64,...`;savedAt 為存入時的 epoch ms。 */
export interface CachedSnapshot {
  eid: string;
  dataUrl: string;
  savedAt: number;
}

/** 本地縮圖自刪年限:90 天(對齊 native)。 */
export const SNAPSHOT_TTL_MS = 90 * 24 * 60 * 60 * 1000;

/** 此環境是否有 IndexedDB(jsdom / SSR 無)。 */
function hasIdb(): boolean {
  try {
    return typeof indexedDB !== 'undefined' && indexedDB != null;
  } catch {
    return false;
  }
}

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const os = db.createObjectStore(STORE, { keyPath: 'eid' });
        os.createIndex('savedAt', 'savedAt', { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('indexedDB open failed'));
  }).catch((e) => {
    // 開啟失敗(隱私模式 / 配額)→ 讓後續呼叫重試,不永久卡住。
    dbPromise = null;
    throw e;
  });
  return dbPromise;
}

function store(db: IDBDatabase, mode: IDBTransactionMode): IDBObjectStore {
  return db.transaction(STORE, mode).objectStore(STORE);
}

function reqToPromise<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('indexedDB request failed'));
  });
}

/** 本地縮圖快取是否可用(此瀏覽器支援 IndexedDB)。 */
export function snapshotCacheAvailable(): boolean {
  return hasIdb();
}

/** 讀一筆縮圖的 data: URL。找不到 / 無 IndexedDB / 讀取失敗 → null。 */
export async function getCachedSnapshot(eid: string): Promise<string | null> {
  if (!eid || !hasIdb()) return null;
  try {
    const db = await openDb();
    const rec = await reqToPromise(store(db, 'readonly').get(eid) as IDBRequest<CachedSnapshot | undefined>);
    return rec?.dataUrl ?? null;
  } catch {
    return null;
  }
}

/** 存一筆縮圖(best-effort)。無 IndexedDB / 配額不足 / 其他失敗一律靜默略過(縮圖顯示照走)。 */
export async function putCachedSnapshot(eid: string, dataUrl: string, now: number = Date.now()): Promise<void> {
  if (!eid || !dataUrl || !hasIdb()) return;
  try {
    const db = await openDb();
    const rec: CachedSnapshot = { eid, dataUrl, savedAt: now };
    await reqToPromise(store(db, 'readwrite').put(rec));
  } catch {
    // 留存失敗不可影響縮圖顯示(仍有 in-memory / 短效 URL 可用)。
  }
}

/**
 * 刪掉存入時間早於 (now - maxAgeMs) 的縮圖(本地 90 天自刪)。回刪除筆數。
 * 開頁時呼叫一次即可;無 IndexedDB / 失敗回 0,不丟例外。
 */
export async function purgeExpiredSnapshots(
  maxAgeMs: number = SNAPSHOT_TTL_MS,
  now: number = Date.now(),
): Promise<number> {
  if (!hasIdb()) return 0;
  try {
    const db = await openDb();
    const os = store(db, 'readwrite');
    const idx = os.index('savedAt');
    // savedAt < cutoff 的全部刪除(upperBound 不含 cutoff 本身)。
    const cutoff = now - maxAgeMs;
    const range = IDBKeyRange.upperBound(cutoff, /*open=*/ true);
    let deleted = 0;
    await new Promise<void>((resolve, reject) => {
      const curReq = idx.openCursor(range);
      curReq.onsuccess = () => {
        const cur = curReq.result;
        if (!cur) return resolve();
        cur.delete();
        deleted++;
        cur.continue();
      };
      curReq.onerror = () => reject(curReq.error ?? new Error('purge cursor failed'));
    });
    return deleted;
  } catch {
    return 0;
  }
}

/** blob → `data:` URL 字串(base64)。供抓下 snapshot bytes 後轉存。 */
export function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(String(fr.result));
    fr.onerror = () => reject(fr.error ?? new Error('blob→dataURL failed'));
    fr.readAsDataURL(blob);
  });
}
